import { baseQuery } from "api/config";
import { endpoints } from "api/endpoints";
import { createApi } from "@reduxjs/toolkit/query/react";
import { defaultTransformResponse } from "helpers/transformReponse";

export const api = createApi({
  baseQuery,
  refetchOnMountOrArgChange: true,
  refetchOnFocus: true,
  tagTypes: [],
  endpoints(build) {
    return {
      applyCoupon: build.mutation({
        query: ({ couponCode, oid }) => ({
          url: endpoints.applyCoupon(oid),
          method: "POST",
          body: {
            couponCode: couponCode,
          },
          transformResponse: defaultTransformResponse,
        }),
      }),
      removeCoupon: build.mutation({
        query: ({ oid, uuid }) => ({
          url: endpoints.removeCoupon(oid, uuid),
          method: "POST",
          transformResponse: defaultTransformResponse,
        }),
      }),
      applyTip: build.mutation({
        query: ({ tip, oid }) => {
          return {
            url: endpoints.applyTip(oid),
            method: "POST",
            body: tip,
            transformResponse: defaultTransformResponse,
          };
        },
      }),
      getOrderDetail: build.query({
        // Force fresh response on every refresh.
        // Without this, the browser HTTP cache can hand back a 304 with an
        // empty body to RTK Query, which makes the order-details fetch
        // resolve with no data and leaves the page stuck on the empty
        // ($0.00 / marketplace-default tips) state until the user reloads
        // a second time.
        query: ({ oId }) => ({
          url: endpoints.orderDetails(oId),
          method: "GET",
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
          },
          transformResponse: defaultTransformResponse,
        }),
      }),
      billingPaymentIntent: build.mutation({
        query: (oId) => ({
          url: endpoints.billingPaymentIntent(),
          method: "POST",
          body: { orderId: oId },
          transformResponse: defaultTransformResponse,
        }),
      }),
      billingPaymentIntentUpdate: build.mutation({
        query: ({ oId, paymentIntentId }) => ({
          url: endpoints.billingPaymentIntentUpdate(),
          method: "PATCH",
          body: { orderId: oId, paymentIntentId: paymentIntentId },
          transformResponse: defaultTransformResponse,
        }),
      }),
      applyPaymentCard: build.mutation({
        query: ({ requestData }) => ({
          url: endpoints.applyPaymentCard(),
          method: "POST",
          body: requestData,
        }),
      }),
      applyPayment: build.mutation({
        query: ({ orderId, amount, tip }) => ({
          url: endpoints.applyPayment(orderId),
          method: "POST",
          body: {
            amount: amount,
            tipInfo: tip,
          },
        }),
      }),
      getPaymentSystem: build.query({
        query: ({ businessId }) => ({
          url: endpoints.getPaymentSystem(businessId),
          method: "GET",
          transformResponse: defaultTransformResponse,
        }),
      }),
      getStripeAccount: build.query({
        query: (businessId) => ({
          url: endpoints.getStripeAccount(businessId),
          method: "GET",
          transformResponse: defaultTransformResponse,
        }),
      }),
      createAdyenSession: build.mutation({
        query: (body) => ({
          url: endpoints.createAdyenSession(),
          method: "POST",
          body,
          headers: body?.businessId ? { business: body.businessId } : undefined,
        }),
      }),
      completeAdyenSession: build.mutation({
        query: (body) => ({
          url: endpoints.completeAdyenSession(),
          method: "POST",
          body,
        }),
      }),
    };
  },
});

export const {
  useLazyGetOrderDetailQuery,
  useApplyCouponMutation,
  useRemoveCouponMutation,
  useApplyTipMutation,
  useBillingPaymentIntentMutation,
  useApplyPaymentMutation,
  useApplyPaymentCardMutation,
  useBillingPaymentIntentUpdateMutation,
  useGetPaymentSystemQuery,
  useLazyGetStripeAccountQuery,
  useCreateAdyenSessionMutation,
  useCompleteAdyenSessionMutation,
} = api;
